import type { DecisionKind, DuplicatePair, PairCounters, PairFilter, PairState, PairsPage } from './types'

/**
 * Решение человека на экране сразу (И-12): как пара и счётчики выглядят после «Это копия», «Не дубли» и «Снять отметку»,
 * ещё до ответа службы. Ответ службы потом заменяет догадку целиком; отказ откатывает её. Здесь только правила
 * «какая пара в каком списке» — те же, что у службы (`DuplicatePairFilters`).
 */

/** Пара числится в списке фильтра: «Все в списке» — неразобранные и отмеченные, «Не дубли» — только в своём фильтре (И-9). */
export function belongsTo(filter: PairFilter, state: PairState): boolean {
  switch (filter) {
    case 'all':
      return state !== 'not-duplicate'
    case 'undecided':
      return state === 'undecided'
    case 'marked':
      return state === 'marked'
    case 'not-duplicate':
      return state === 'not-duplicate'
  }
}

function withState(counters: PairCounters, state: PairState, delta: number): PairCounters {
  const undecided = counters.undecided + (state === 'undecided' ? delta : 0)
  const marked = counters.marked + (state === 'marked' ? delta : 0)
  const notDuplicate = counters.notDuplicate + (state === 'not-duplicate' ? delta : 0)
  return { all: undecided + marked, undecided, marked, notDuplicate }
}

/** Счётчики после перехода одной пары из состояния `from` в `to`. */
export function countersAfter(counters: PairCounters, from: PairState, to: PairState): PairCounters {
  return from === to ? counters : withState(withState(counters, from, -1), to, 1)
}

/** Пара, какой её ждёт человек после команды. `key` — выбранный товар для «Это копия». */
export function predictPair(pair: DuplicatePair, kind: DecisionKind, key: string | null): DuplicatePair {
  switch (kind) {
    case 'copy':
      return { ...pair, state: 'marked', copySide: key === pair.first.key ? 'first' : key === pair.second.key ? 'second' : pair.copySide }
    case 'not-duplicate':
      return { ...pair, state: 'not-duplicate', copySide: null }
    case 'reset':
      return { ...pair, state: 'undecided', copySide: null }
  }
}

/**
 * Страница после того, как пара стала `pair`, а счётчики — `counters`: пара заменяется на месте или уходит из списка, если в этом
 * фильтре ей больше не место. Пары на странице нет (ушла раньше, страница сменилась) — меняются только счётчики.
 */
export function applyPair(page: PairsPage, pair: DuplicatePair, counters: PairCounters): PairsPage {
  const present = page.items.some((item) => item.id === pair.id)
  if (!present) return { ...page, counters }
  const stays = belongsTo(page.filter, pair.state)
  return {
    ...page,
    counters,
    total: stays ? page.total : Math.max(0, page.total - 1),
    items: stays ? page.items.map((item) => (item.id === pair.id ? pair : item)) : page.items.filter((item) => item.id !== pair.id),
  }
}

/** Догадка до ответа службы: пара из кэша страницы → её будущий вид и счётчики. `null` — пары на странице нет. */
export function predictPage(page: PairsPage, pairId: number, kind: DecisionKind, key: string | null): PairsPage | null {
  const current = page.items.find((item) => item.id === pairId)
  if (current === undefined) return null
  const next = predictPair(current, kind, key)
  return applyPair(page, next, countersAfter(page.counters, current.state, next.state))
}
